import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useState } from "react";
import {
  FitnessProfileAlgorithm,
  FitnessProfileType,
  ThreeDPQuizResultsType,
  WahooWorkoutTypeFamily,
} from "@WahooFitness/cloud-client-types";
import { getRunningLegendItems } from "@/components/FitnessProfile/Shared/legendItems";
import {
  postClose,
  useDrawerContext,
  useGuardedUpdater,
  useHeaderContext,
} from "@WahooFitness/wahoo-offline-mfe";
import { t } from "@lingui/macro";
import useFitnessProfiles from "@/hooks/useFitnessProfiles";
import useUserProfiles from "@/hooks/useUserProfiles";
import { useNavigate } from "react-router";
import { TrendIcon } from "@WahooFitness/redesignr";
import { useLogEvent } from "@/services/Analytics/useLogEvent";
import { ClickTarget, SourcePage } from "@/services/Analytics/EventTypes";
import useLocalizedRunningFitnessData from "@/components/FitnessProfile/RunningProfile/useLocalizedRunningFitnessData";
import DiscardResultsDrawer from "../../Results/DiscardResultsDrawer";
import ThreeDPPages from "../ThreeDPPages";
import { useTheme } from "@mui/material";
import HowWeCalculateDrawer from "@/components/FitnessProfile/HowWeCalculateDrawer";

const useThreeDPQuizResults = (
  setPage: Dispatch<SetStateAction<number>>,
  results?: ThreeDPQuizResultsType,
  closeOnBack?: boolean
) => {
  // TODO: update resultsLoading logic as part of PEAR-1565
  const [resultsLoading, setResultsLoading] = useState(false);
  const { update } = useGuardedUpdater();
  const { logEvent } = useLogEvent();
  const { setDrawer } = useDrawerContext();
  const { navigateBack } = useHeaderContext();
  const navigate = useNavigate();
  const { palette } = useTheme();

  useEffect(() => {
    setResultsLoading(true);
    const timeoutId = setTimeout(() => {
      setResultsLoading(false);
    }, 2000);
    return () => clearTimeout(timeoutId);
  }, []);

  const reloadHandler = useCallback(() => {
    navigate("/fitness-profile");
  }, [navigate]);

  const { getRunnerTypeById, getRunnerTypeStrengthById, getRunnerTypeWeaknessById } =
    useLocalizedRunningFitnessData();

  const runnerTypeData = useMemo(
    () => getRunnerTypeById(results?.profile?.athlete?.type),
    [results, getRunnerTypeById]
  );

  const runnerStrength = useMemo(() => {
    const data = getRunnerTypeStrengthById(results?.profile?.athlete?.strength);
    if (data) {
      return {
        name: data.name,
        description: data.description,
        icon: <TrendIcon size="small" direction="up" color="success" />,
      };
    }
  }, [results, getRunnerTypeStrengthById]);

  const runnerWeakness = useMemo(() => {
    const data = getRunnerTypeWeaknessById(results?.profile?.athlete?.weakness);
    if (data) {
      return {
        name: data.name,
        description: data.description,
        icon: <TrendIcon size="small" direction="down" color="error" />,
      };
    }
  }, [results, getRunnerTypeWeaknessById]);

  const { runningFitnessProfile, createOrUpdateFitnessProfile } = useFitnessProfiles();
  const { userProfilesData, updateUserProfiles } = useUserProfiles();

  const saveFitnessProfile = useCallback(async () => {
    if (!results?.profile) return;
    logEvent("3dp_quiz", {
      click_target: ClickTarget.save,
      source_page_name: SourcePage.threedp_quiz_results,
    });
    const fitnessProfileUpdate: Omit<
      FitnessProfileType,
      "user_id" | "dur_1_src" | "dur_2_src" | "dur_3_src" | "dur_4_src"
    > = {
      workout_type_family_id: WahooWorkoutTypeFamily.Running,
      fitness_type_id: results.profile.athlete.type,
      fitness_weakness_id: results.profile.athlete.weakness,
      fitness_strength_id: results.profile.athlete.strength,
      dim_1: results.profile.threedp.FTP,
      dim_2: results.profile.threedp.MAP,
      dim_3: results.profile.threedp.AC,
      dim_4: null,
      dim_1_rank: results.profile.rank.FTP,
      dim_2_rank: results.profile.rank.MAP,
      dim_3_rank: results.profile.rank.AC,
      dim_4_rank: null,
      fitness_profile_algorithm_id: FitnessProfileAlgorithm.ThreeDPQuiz,
      threshold_hr: runningFitnessProfile?.threshold_hr ?? null,
      preferred_cadence: runningFitnessProfile?.preferred_cadence ?? null,
      dur_1: results.profile.known_times?.five_k_time_seconds ?? null,
      dur_2: results.profile.known_times?.ten_k_time_seconds ?? null,
      dur_3: results.profile.known_times?.half_marathon_time_seconds ?? null,
      dur_4: results.profile.known_times?.marathon_time_seconds ?? null,
    };

    return update(
      () => createOrUpdateFitnessProfile(fitnessProfileUpdate, runningFitnessProfile?.id),
      {
        afterUpdate: async () => {
          if (!userProfilesData?.workout_type_family_preferences?.length) {
            await updateUserProfiles({
              workoutTypeFamilyPreferences: [
                WahooWorkoutTypeFamily.Cycling,
                WahooWorkoutTypeFamily.Running,
              ],
            });
          } else if (
            !userProfilesData.workout_type_family_preferences.includes(
              WahooWorkoutTypeFamily.Running
            )
          ) {
            await updateUserProfiles({
              workoutTypeFamilyPreferences: [
                ...userProfilesData.workout_type_family_preferences,
                WahooWorkoutTypeFamily.Running,
              ],
            });
          }
          if (closeOnBack) {
            postClose("threedp");
          } else {
            navigateBack();
          }
        },
        errorMessage: t`There was a problem saving your profile. Check your network and try again.`,
      }
    );
  }, [
    closeOnBack,
    logEvent,
    navigateBack,
    results?.profile,
    runningFitnessProfile,
    update,
    createOrUpdateFitnessProfile,
    userProfilesData,
    updateUserProfiles,
  ]);

  const handleCloseDrawer = useCallback(() => {
    setDrawer({ open: false });
  }, [setDrawer]);

  const handleDiscardResults = useCallback(() => {
    logEvent("3dp_quiz", {
      click_target: ClickTarget.discard_results,
      source_page_name: SourcePage.threedp_quiz_results,
    });
    setDrawer({ open: false });
    if (closeOnBack) {
      postClose("threedp");
    } else {
      navigateBack();
    }
  }, [closeOnBack, logEvent, navigateBack, setDrawer]);

  const discardResults = useCallback(() => {
    setDrawer({
      open: true,
      hidePuller: true,
      children: (
        <DiscardResultsDrawer onCancel={handleCloseDrawer} onDiscard={handleDiscardResults} />
      ),
      elevation: 4,
      hideHeader: true,
    });
  }, [handleCloseDrawer, handleDiscardResults, setDrawer]);

  const navigateToQuiz = useCallback(() => {
    setPage(ThreeDPPages.quiz);
  }, [setPage]);

  const dimensions = useMemo(() => {
    return {
      tp: {
        value: results?.profile.threedp.FTP ?? 0,
        rank: results?.profile.rank.FTP ?? 0,
      },
      map: {
        value: results?.profile.threedp.MAP ?? 0,
        rank: results?.profile.rank.MAP ?? 0,
      },
      ac: {
        value: results?.profile.threedp.AC ?? 0,
        rank: results?.profile.rank.AC ?? 0,
      },
    };
  }, [
    results?.profile.threedp.FTP,
    results?.profile.rank.FTP,
    results?.profile.threedp.MAP,
    results?.profile.rank.MAP,
    results?.profile.threedp.AC,
    results?.profile.rank.AC,
  ]);

  const legendItems = useMemo(() => getRunningLegendItems(palette), [palette]);

  const analyticsContext = useMemo(
    () => ({
      name: "3dp_quiz",
      data: { source_page_name: SourcePage.threedp_quiz_results },
    }),
    []
  );

  const openInfoDrawer = useCallback(() => {
    logEvent("3dp_quiz", {
      click_target: ClickTarget.how_we_calculate,
      source_page_name: SourcePage.threedp_quiz_results,
    });
    setDrawer({
      open: true,
      title: t`How we calculate?`,
      hidePuller: true,
      children: (
        <HowWeCalculateDrawer
          infoText={t`This chart visualizes your Athlete Profile and compares how each metric ranks to one another for your specific demographic. It factors in your height, weight, age, sex and fitness level to determine your abilities. If a value falls in the modest range, it highlights areas for improvement. If the value falls in the exceptional range, it highlights your strengths as a runner.`}
        />
      ),
      elevation: 4,
    });
  }, [logEvent, setDrawer]);

  return {
    resultsLoading,
    runnerTypeData,
    runnerStrength,
    runnerWeakness,
    saveFitnessProfile,
    discardResults,
    reloadHandler,
    navigateToQuiz,
    dimensions,
    legendItems,
    analyticsContext,
    openInfoDrawer,
  };
};

export default useThreeDPQuizResults;
